import { useEffect, useState } from 'react';
import api from '@/lib/api';

// Single source of truth for UI enums, mirrored from GET /config/enums. We
// keep local fallbacks so the UI still renders offline or if the endpoint
// fails - but whenever the server responds, the server values win.
//
// Usage:
//   - `await loadEnums()` once on app boot (non-blocking).
//   - `getEnums()` anywhere, returns the cached value synchronously.
//   - `useEnums()` React hook: subscribes to cache changes.

export const FALLBACK_ENUMS = Object.freeze({
  application_statuses: ['draft', 'submitted', 'approved', 'in_search', 'matched', 'archived', 'rejected'],
  application_status_transitions: {},
  listing_proposal_statuses: ['proposed', 'interested', 'declined', 'withdrawn'],
  listing_proposal_tenant_replies: ['interested', 'declined'],
  listing_proposal_pipeline_stages: [
    'new', 'email_sent', 'email_opened', 'matches_page_opened', 'listing_viewed',
    'interested', 'visit_requested', 'visit_scheduled', 'declined', 'withdrawn',
  ],
  tenant_matches_path: '/dashboard?tab=matches',
  notification_types: ['listing.proposed', 'dossier.pushed'],
  email_event_keys: [],
  email_draft_statuses: ['pending', 'sent', 'skipped', 'failed'],
  email_recipient_types: ['tenant', 'pm', 'custom'],
  stage_email_event_keys: {},
  supported_languages: ['en', 'fr'],
});

let cache = { ...FALLBACK_ENUMS };
let loaded = false;
let inflight = null;
const subscribers = new Set();

export function getEnums() {
  return cache;
}

export function isEnumsLoaded() {
  return loaded;
}

export async function loadEnums() {
  if (loaded) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await api.get('/config/enums');
      cache = { ...FALLBACK_ENUMS, ...(res.data || {}) };
      loaded = true;
      subscribers.forEach((fn) => { try { fn(cache); } catch {} });
      // Drift detector: warn if fallback list is out of sync with server.
      const mismatch = (key) => {
        const s = new Set(cache[key] || []);
        const f = new Set(FALLBACK_ENUMS[key] || []);
        const missingInFallback = [...s].filter((x) => !f.has(x));
        const extraInFallback = [...f].filter((x) => !s.has(x));
        if (missingInFallback.length || extraInFallback.length) {
          // eslint-disable-next-line no-console
          console.warn(
            `[enums] ${key} drift detected. server=${[...s].join(',')} fallback=${[...f].join(',')}`
          );
        }
      };
      ['application_statuses', 'listing_proposal_statuses', 'notification_types', 'email_draft_statuses'].forEach(mismatch);
      return cache;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[enums] /config/enums fetch failed, using fallback', err?.message);
      return cache;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function subscribeEnums(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

// Convenience hook for components that need to re-render when enums load.
// Safe to call before loadEnums() has completed - it will return the
// FALLBACK_ENUMS immediately and update once the fetch resolves.
export function useEnums() {
  const [state, setState] = useState(cache);
  useEffect(() => {
    let cancelled = false;
    if (!loaded) {
      loadEnums().then((next) => {
        if (!cancelled) setState(next);
      });
    }
    const unsub = subscribeEnums((next) => {
      if (!cancelled) setState(next);
    });
    return () => { cancelled = true; unsub(); };
  }, []);
  return state;
}
